import { useEffect, useMemo } from 'react';
import { useToolPreferences } from 'hooks/usePreferences';
import { sendCrossTabEvent, NameSpaces } from 'modules/EventsManager';
import Mousetrap from 'mousetrap';
import { unreachableCaseError } from 'types';
import type { Shortcut } from 'generated/graphql';
import {
  measureReportNavigationEnd,
  measureReportNavigationStart,
  ReportNavigationDirection,
  ReportNavigationMethod,
} from 'domains/reporter/RichTextEditor/analytics/performance';
import { WithApolloContext } from 'modules/Apollo/types';
import { logger } from 'modules/logger';
import { getPageType } from 'utils/pageTypes';
import { normalizeMousetrapCombo, bindMousetrap, unbindMousetrap } from 'utils/mousetraps';

export type ReporterShortcutID =
  | 'REPORTER_TOGGLE_DICTATION'
  | 'REPORTER_PREVIOUS'
  | 'REPORTER_NEXT'
  | 'REPORTER_SUBMIT_REPORT'
  | 'REPORTER_GENERATE_IMPRESSION'
  | 'REPORTER_DISCARD_REPORT'
  | 'REPORTER_DRAFT_REPORT'
  | 'REPORTER_COMMAND_MODE';

export const REPORTER_SHORTCUT_IDS = [
  'REPORTER_TOGGLE_DICTATION',
  'REPORTER_PREVIOUS',
  'REPORTER_NEXT',
  'REPORTER_SUBMIT_REPORT',
  'REPORTER_GENERATE_IMPRESSION',
  'REPORTER_DISCARD_REPORT',
  'REPORTER_DRAFT_REPORT',
  'REPORTER_COMMAND_MODE',
] as const satisfies readonly ReporterShortcutID[];

export const SELECTION_MODIFYING_REPORTER_SHORTCUT_IDS = [
  'REPORTER_PREVIOUS',
  'REPORTER_NEXT',
] as const satisfies readonly ReporterShortcutID[];

const logReporterShortcutTrigger = ({
  shortcutId,
  configuredKey,
  action,
  combo,
}: {
  shortcutId: string;
  configuredKey: string;
  action: string;
  combo?: string;
}) => {
  logger.info('[useCrossWindowReporterShortcuts] shortcut triggered', {
    shortcutId,
    // Raw stored preference (may be legacy modifier-last order, e.g. "F10+alt+ctrl+shift").
    configuredKey,
    // The combo we actually bind/fire on. Mousetrap does not reliably pass its own
    // `combo` arg for these bindings (it arrives undefined), so fall back to the same
    // normalization applied at bind time so the log reflects real runtime behavior (RP-5539).
    combo: combo ?? normalizeMousetrapCombo(configuredKey),
    action,
    originPageType: getPageType(window.location.href),
  });
};

const generateReportHandlerByID = (
  shortcutID: string,
  configuredKey: string
): /**
 * Mousetrap sends an empty object in place of a proper Event instance on Node,
 * so we have to check for that not to creash during unit tests
 */
((e: Event | Record<any, any>, combo?: string) => void) => {
  switch (shortcutID) {
    case 'REPORTER_TOGGLE_DICTATION':
      return (evt: Event | Record<any, any>, combo?: string) => {
        if (evt instanceof Event) evt.preventDefault();
        logReporterShortcutTrigger({
          shortcutId: shortcutID,
          configuredKey,
          action: 'toggleRecording',
          combo,
        });
        sendCrossTabEvent(NameSpaces.REPORTER_SHORTCUT, {
          type: 'keyPress',
          action: 'toggleRecording',
        });
      };
    case 'REPORTER_PREVIOUS':
      return (evt: Event | Record<any, any>, combo?: string) => {
        if (evt instanceof Event) evt.preventDefault();
        logReporterShortcutTrigger({
          shortcutId: shortcutID,
          configuredKey,
          action: 'previousBracket',
          combo,
        });
        measureReportNavigationStart();
        sendCrossTabEvent(NameSpaces.REPORTER_SHORTCUT, {
          type: 'keyPress',
          action: 'previousBracket',
        });
        measureReportNavigationEnd({
          direction: ReportNavigationDirection.Back,
          method: ReportNavigationMethod.KeyboardShortcut,
        });
      };
    case 'REPORTER_NEXT':
      return (evt: Event | Record<any, any>, combo?: string) => {
        if (evt instanceof Event) evt.preventDefault();
        logReporterShortcutTrigger({
          shortcutId: shortcutID,
          configuredKey,
          action: 'nextBracket',
          combo,
        });

        measureReportNavigationStart();
        sendCrossTabEvent(NameSpaces.REPORTER_SHORTCUT, {
          type: 'keyPress',
          action: 'nextBracket',
        });
        measureReportNavigationEnd({
          direction: ReportNavigationDirection.Forward,
          method: ReportNavigationMethod.KeyboardShortcut,
        });
      };
    case 'REPORTER_SUBMIT_REPORT':
      return (evt: Event | Record<any, any>, combo?: string) => {
        if (evt instanceof Event) evt.preventDefault();
        logReporterShortcutTrigger({
          shortcutId: shortcutID,
          configuredKey,
          action: 'submitReport',
          combo,
        });
        sendCrossTabEvent(NameSpaces.REPORTER_SHORTCUT, {
          type: 'keyPress',
          action: 'submitReport',
        });
      };

    case 'REPORTER_GENERATE_IMPRESSION':
      return (evt: Event | Record<any, any>, combo?: string) => {
        if (evt instanceof Event) evt.preventDefault();
        logReporterShortcutTrigger({
          shortcutId: shortcutID,
          configuredKey,
          action: 'generateImpression',
          combo,
        });
        sendCrossTabEvent(NameSpaces.REPORTER_SHORTCUT, {
          type: 'keyPress',
          action: 'generateImpression',
        });
      };

    case 'REPORTER_DISCARD_REPORT':
      return (evt: Event | Record<any, any>, combo?: string) => {
        if (evt instanceof Event) evt.preventDefault();
        logReporterShortcutTrigger({
          shortcutId: shortcutID,
          configuredKey,
          action: 'discardReport',
          combo,
        });
        sendCrossTabEvent(NameSpaces.REPORTER_SHORTCUT, {
          type: 'keyPress',
          action: 'discardReport',
        });
      };

    case 'REPORTER_DRAFT_REPORT':
      return (evt: Event | Record<any, any>, combo?: string) => {
        if (evt instanceof Event) evt.preventDefault();
        logReporterShortcutTrigger({
          shortcutId: shortcutID,
          configuredKey,
          action: 'draftReport',
          combo,
        });
        sendCrossTabEvent(NameSpaces.REPORTER_SHORTCUT, {
          type: 'keyPress',
          action: 'draftReport',
        });
      };

    case 'REPORTER_COMMAND_MODE':
      return (evt: Event | Record<any, any>, combo?: string) => {
        if (evt instanceof Event) evt.preventDefault();
        logReporterShortcutTrigger({
          shortcutId: shortcutID,
          configuredKey,
          action: 'toggleCommandMode',
          combo,
        });
        sendCrossTabEvent(NameSpaces.REPORTER_SHORTCUT, {
          type: 'keyPress',
          action: 'toggleCommandMode',
        });
      };

    default:
      return unreachableCaseError(shortcutID);
  }
};

export const generateGlobalReporterShortcutsHandlers = (
  shortcuts: ReadonlyArray<Readonly<Shortcut>>
): {
  key: string;
  callback: (e: Event, combo?: string) => void;
  id: string;
}[] => {
  const shortcutsById = new Map(shortcuts.map((shortcut) => [shortcut.id, shortcut]));

  return REPORTER_SHORTCUT_IDS.map((shortcutId) => shortcutsById.get(shortcutId))
    .filter((shortcut): shortcut is Shortcut => shortcut != null)
    .map((shortcut) => ({
      key: shortcut.key ?? '',
      callback: generateReportHandlerByID(shortcut.id, shortcut.key ?? ''),
      id: shortcut.id,
    }));
};

export const useCrossWindowReporterShortcuts = ({ context }: WithApolloContext) => {
  const [toolPreferences] = useToolPreferences({ context });

  const reporterGlobalShortcutHandlers = useMemo(
    () =>
      toolPreferences
        ? generateGlobalReporterShortcutsHandlers(toolPreferences.keyboard.shortcuts)
        : [],
    [toolPreferences]
  );

  useEffect(() => {
    reporterGlobalShortcutHandlers.forEach(({ key, callback }) => {
      if (!key?.trim()) return;
      bindMousetrap(Mousetrap, key, callback);
    });

    return () => {
      reporterGlobalShortcutHandlers.forEach(({ key, callback }) => {
        if (!key?.trim()) return;
        unbindMousetrap(Mousetrap, key);
      });
    };
  }, [reporterGlobalShortcutHandlers]);
};
